import { useCallback, useEffect, useMemo } from 'react';
import { theme } from '@frontend/theme';
import { formatFieldLabel } from '../../../../helpers';
import { FormActionsFooter } from '../../../layout/form-actions-footer.component';
import { AddressField } from './fields/address-field';
import { CardCaptureField } from './fields/card-capture-field';
import { ConditionalField } from './fields/conditional-field';
import { ESignField } from './fields/esign-field';
import { ImageUploadField } from './fields/image-upload-field';
import { InsuranceVerificationField } from './fields/insurance-verification-field/insurance-verification-field';
import { PaymentOnFileField } from './fields/payment-on-file-field';
import { PdfField } from './fields/pdf-field';
import { RichTextField } from './fields/rich-text-field/rich-text-field';
import { SsnFormField } from './fields/ssn-field';
import { getDefaultValuesObject, getFieldValidator } from './helpers';
import { DynamicFormApi, useDynamicForm } from './hooks/use-app-form';
import { useDynamicFormStore } from './store';
import type { DynamicFormField } from './types';

interface Props {
  sectionId: string;
  virtualFieldIds: string[];
  showPreviousButton?: boolean;
  prevDisabled?: boolean;
  nextDisabled?: boolean;
  onNext: (values: Record<string, unknown>) => void;
  onPrevious: (values: Record<string, unknown>) => void;
  onChangeListener?: (values: Record<string, unknown>) => void;
  nextButtonText?: string;
  previousButtonText?: string;
}

export const DynamicForm = ({
  sectionId,
  virtualFieldIds,
  showPreviousButton = true,
  prevDisabled,
  nextDisabled,
  onNext,
  onPrevious,
  onChangeListener,
  nextButtonText,
  previousButtonText,
}: Props) => {
  const { formFieldsMap } = useDynamicFormStore(['formFieldsMap']);
  const sectionFields = useMemo(
    () => virtualFieldIds.map((id) => formFieldsMap[id]).filter(Boolean),
    [formFieldsMap, virtualFieldIds]
  );
  const defaultValues = useMemo(() => getDefaultValuesObject(sectionFields), [sectionFields]);

  const form = useDynamicForm({
    sectionId,
    defaultValues,
    onNext,
    onChangeListener,
  });

  const handlePrevClick = useCallback(() => {
    onPrevious(form.state.values);
  }, [form.state.values, onPrevious]);

  // this component remounts per section (see `key` in dynamic-form-container.tsx), so this only fires on successful navigation
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <form
      key='One'
      id={`dynamic-form-${sectionId}`}
      onSubmit={(e) => {
        e.preventDefault();
        form.handleSubmit();
      }}
      css={{ display: 'flex', flexDirection: 'column', alignItems: 'stretch', gap: theme.spacing(1.5) }}
    >
      {sectionFields.map((field) => (
        <ConditionalField key={field.key} form={form} fieldInfo={field}>
          {renderField(form, field)}
        </ConditionalField>
      ))}

      <form.Subscribe selector={(s) => s.isSubmitting}>
        {(isSubmitting) => (
          <FormActionsFooter
            showPreviousButton={showPreviousButton}
            prevDisabled={prevDisabled}
            nextDisabled={nextDisabled}
            isSubmitting={isSubmitting}
            previousButtonText={previousButtonText}
            nextButtonText={nextButtonText}
            onPreviousClick={handlePrevClick}
          />
        )}
      </form.Subscribe>
    </form>
  );
};

const renderField = (form: DynamicFormApi, field: DynamicFormField) => {
  switch (field.meta.type) {
    case 'cardCapture':
      return <CardCaptureField form={form} fieldInfo={field} />;
    case 'cardOnFile':
    case 'achOnFile':
      return <PaymentOnFileField form={form} fieldInfo={field} type={field.meta.type} />;
    case 'insuranceVerification':
      return <InsuranceVerificationField form={form} fieldInfo={field} />;
    case 'imageUpload':
      return <ImageUploadField form={form} fieldInfo={field} />;
    case 'eSign':
      return <ESignField form={form} fieldInfo={field} />;
    case 'pdf':
      return <PdfField form={form} fieldInfo={field} />;
    case 'address':
      return <AddressField form={form} fieldInfo={field} />;
    case 'ssn':
      return <SsnFormField form={form} fieldInfo={field} />;
    case 'richText':
      return <RichTextField fieldKey={field.key} htmlContent={field.value as string} />;
    default:
      return (
        <form.AppField name={field.key} validators={{ onChange: ({ value }) => getFieldValidator(field, value) }}>
          {(formField) => (
            <formField.FormField
              label={formatFieldLabel(field.label, field.required)}
              type={field.meta.type}
              fieldKey={field.key}
              options={field.options}
              categories={field.categories}
              fieldMeta={field.fieldMeta}
            />
          )}
        </form.AppField>
      );
  }
};
